import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Ein Aufklappmenü nach dem anderen.
 *
 * Auf dem Board liegen die Auslöser dicht beieinander: die Einfügen-Leiste
 * unten, die Frame-Leiste am ausgewählten Frame, die Farbwähler darin, die
 * Teilnehmerliste oben. Jedes Menü brachte bisher sein eigenes Schließen mit —
 * und keines wusste vom anderen. Es ließen sich mehrere gleichzeitig
 * aufklappen, die sich dann gegenseitig verdeckten.
 *
 * Der Riegel liegt bewusst *außerhalb* von React: Die Menüs stehen in ganz
 * verschiedenen Teilbäumen — auf der Leinwand, in der Kopfleiste, in Dialogen —
 * und ein gemeinsamer Kontext müsste sie alle umschließen. Ein Modul-weiter
 * Wert mit Zuhörerliste tut dasselbe, ohne den Baum umzubauen.
 */

/** Welches Menü gerade offen ist — erkannt an seiner Marke, nicht am Namen. */
let offenes: object | null = null
const zuhoerer = new Set<() => void>()

function riegeln(marke: object | null): void {
  if (offenes === marke) return
  offenes = marke
  // Kopie: Ein Zuhörer darf sich beim Melden abmelden.
  for (const melden of [...zuhoerer]) melden()
}

export interface Popover<T extends HTMLElement> {
  /** Ist *dieses* Menü gerade offen? */
  open: boolean
  /** Auslöser: öffnet dieses Menü und schließt damit jedes andere. */
  toggle: () => void
  close: () => void
  /**
   * Gehört an die Wurzel von Auslöser *und* Menü: Woanders hin zu klicken
   * schließt, hier hinein nicht.
   */
  ref: React.RefObject<T | null>
}

export function usePopover<T extends HTMLElement = HTMLDivElement>(): Popover<T> {
  const marke = useRef({}).current
  const ref = useRef<T | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const melden = (): void => setOpen(offenes === marke)
    zuhoerer.add(melden)
    return () => {
      zuhoerer.delete(melden)
      // Verschwindet der Auslöser, während sein Menü offen steht — etwa weil
      // die Auswahl aufgehoben wurde —, bliebe der Riegel sonst für immer
      // liegen und kein anderes Menü ginge je wieder auf.
      riegeln(offenes === marke ? null : offenes)
    }
  }, [marke])

  const close = useCallback((): void => {
    if (offenes === marke) riegeln(null)
  }, [marke])

  const toggle = useCallback((): void => {
    riegeln(offenes === marke ? null : marke)
  }, [marke])

  useEffect(() => {
    if (!open) return
    const beiTaste = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      // Sonst hebt dieselbe Taste zusätzlich die Auswahl auf dem Board auf.
      event.stopPropagation()
      riegeln(null)
    }
    const beiZeiger = (event: PointerEvent): void => {
      if (!ref.current?.contains(event.target as Node)) riegeln(null)
    }
    /*
     * Beide Zuhörer in der Capture-Phase. Die Bedienleisten auf der Leinwand
     * halten `pointerdown` an, damit ein Klick darauf nicht das Board
     * verschiebt — in der Blasenphase käme das Schließen dort nie an, und genau
     * daher rührten die übereinanderliegenden Menüs.
     */
    window.addEventListener('keydown', beiTaste, true)
    window.addEventListener('pointerdown', beiZeiger, true)
    return () => {
      window.removeEventListener('keydown', beiTaste, true)
      window.removeEventListener('pointerdown', beiZeiger, true)
    }
  }, [open])

  return { open, toggle, close, ref }
}
